import { TabletButton } from '@campfire/tablet-button';
import { Box, Switch, TextField, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import {
  GET_ADMIN_CONSOLE_OPS_ADDRESS_HIDING,
  GET_ADMIN_CONSOLE_OPS_ADDRESS_REMOVAL,
  GET_ADMIN_CONSOLE_OPS_ADDRESS_ALLOW_DEFAULT,
  GET_ADMIN_CONSOLE_OPS_ADDRESS_HIDE_EXEMPT_PM,
  GET_ADMIN_CONSOLE_OPS_ADDRESS_HIDE_EXEMPT_AL,
} from './admin-console-operations.gql';

export function AddressHiding() {
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const { theme } = useCampfireTheme();
  const campfireMutation = useCampfireFetch({ defer: true });

  const { data, refetch } = useCampfireQuery<any, undefined>(GET_ADMIN_CONSOLE_OPS_ADDRESS_HIDING);
  const { data: removalData, refetch: removalRefetch } = useCampfireQuery<any, undefined>(
    GET_ADMIN_CONSOLE_OPS_ADDRESS_REMOVAL
  );
  const { data: allowDefaultData, refetch: allowDefaultRefetch } = useCampfireQuery<any, undefined>(
    GET_ADMIN_CONSOLE_OPS_ADDRESS_ALLOW_DEFAULT
  );
  const { data: exemptPmData, refetch: exemptPmRefetch } = useCampfireQuery<any, undefined>(
    GET_ADMIN_CONSOLE_OPS_ADDRESS_HIDE_EXEMPT_PM
  );
  const { data: exemptAlData, refetch: exemptAlRefetch } = useCampfireQuery<any, undefined>(
    GET_ADMIN_CONSOLE_OPS_ADDRESS_HIDE_EXEMPT_AL
  );

  const [enabled, setEnabled] = useState(false);
  const [removalEnabled, setRemovalEnabled] = useState(false);
  const [allowDefaultEnabled, setAllowDefaultEnabled] = useState(false);
  const [exemptPm, setExemptPm] = useState(false);
  const [exemptAl, setExemptAl] = useState(false);
  const [revealDays, setRevealDays] = useState<number | ''>('');
  const [hideDays, setHideDays] = useState<number | ''>('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const serverRevealDays = data?.tm.tenant?.config?.addressRevealBeforeDays;
    const serverHideDays = data?.tm.tenant?.config?.addressHideAfterDays;
    if (serverRevealDays !== undefined && serverRevealDays !== null) {
      setEnabled(true);
      setRevealDays(serverRevealDays);
    } else if (serverHideDays !== undefined && serverHideDays !== null) {
      setEnabled(true);
    } else {
      setEnabled(false);
    }
    if (serverHideDays !== undefined && serverHideDays !== null) {
      setHideDays(serverHideDays);
    } else {
      setHideDays('');
    }
    if (serverRevealDays === undefined || serverRevealDays === null) {
      if (!enabled) {
        setRevealDays('');
      }
    }
    setHasChanges(false);
  }, [data]);

  useEffect(() => {
    setRemovalEnabled(removalData?.vm.configItem?.configItemValue?.flagValue ?? false);
  }, [removalData]);

  useEffect(() => {
    setAllowDefaultEnabled(allowDefaultData?.vm.configItem?.configItemValue?.flagValue ?? false);
  }, [allowDefaultData]);

  useEffect(() => {
    setExemptPm(exemptPmData?.vm.configItem?.configItemValue?.flagValue ?? false);
    setExemptAl(exemptAlData?.vm.configItem?.configItemValue?.flagValue ?? false);
  }, [exemptPmData, exemptAlData]);

  const onRemovalChange = () => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/admin-console/config`,
        method: 'post',
        data: {
          value: !removalEnabled,
          type: 'address-remove-after-event',
        },
      })
      .then(() => {
        if (removalRefetch) {
          setTimeout(removalRefetch, 0);
        }
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Address removal settings updated',
        });
      });
    setRemovalEnabled(!removalEnabled);
  };

  const onAllowDefaultChange = () => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/admin-console/config`,
        method: 'post',
        data: {
          value: !allowDefaultEnabled,
          type: 'address-allow-default-display',
        },
      })
      .then(() => {
        if (allowDefaultRefetch) {
          setTimeout(allowDefaultRefetch, 0);
        }
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Allow default address display settings updated',
        });
      });
    setAllowDefaultEnabled(!allowDefaultEnabled);
  };

  const onExemptPmChange = () => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/admin-console/config`,
        method: 'post',
        data: {
          value: !exemptPm,
          type: 'address-hide-exempt-pm',
        },
      })
      .then(() => {
        if (exemptPmRefetch) {
          setTimeout(exemptPmRefetch, 0);
        }
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Address visibility exemption updated',
        });
      });
    setExemptPm(!exemptPm);
  };

  const onExemptAlChange = () => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/admin-console/config`,
        method: 'post',
        data: {
          value: !exemptAl,
          type: 'address-hide-exempt-al',
        },
      })
      .then(() => {
        if (exemptAlRefetch) {
          setTimeout(exemptAlRefetch, 0);
        }
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Address visibility exemption updated',
        });
      });
    setExemptAl(!exemptAl);
  };

  const handleToggle = () => {
    setEnabled(!enabled);
    if (enabled) {
      setRevealDays('');
      setHideDays('');
    }
    setHasChanges(true);
  };

  const handleRevealDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setRevealDays('');
    } else {
      const numValue = parseInt(e.target.value, 10);
      if (!Number.isNaN(numValue)) {
        setRevealDays(numValue);
      }
    }
    setHasChanges(true);
  };

  const handleHideDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setHideDays('');
    } else {
      const numValue = parseInt(e.target.value, 10);
      if (!Number.isNaN(numValue)) {
        setHideDays(numValue);
      }
    }
    setHasChanges(true);
  };

  const handleSave = () => {
    const revealToSave = enabled && revealDays !== '' ? revealDays : null;
    const hideToSave = enabled && hideDays !== '' ? hideDays : null;

    campfireMutation
      .run({
        url: `${apiUrl}/tm/config/address-hiding`,
        method: 'post',
        data: {
          revealBeforeDays: revealToSave,
          hideAfterDays: hideToSave,
        },
      })
      .then(() => {
        if (refetch) {
          setTimeout(refetch, 0);
        }
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Address hiding settings updated',
        });
        setHasChanges(false);
      })
      .catch(() => {
        setSnackbar({
          variant: 'error',
          open: true,
          message: 'Failed to update address hiding settings',
        });
      });
  };

  const isValidRevealDays =
    revealDays === '' || (typeof revealDays === 'number' && revealDays >= 1 && revealDays <= 30);
  const isValidHideDays = hideDays === '' || (typeof hideDays === 'number' && hideDays >= 1 && hideDays <= 30);
  const hasAtLeastOneValue = revealDays !== '' || hideDays !== '';
  const canSave = hasChanges && (!enabled || (enabled && hasAtLeastOneValue && isValidRevealDays && isValidHideDays));

  return (
    <Box mt={2}>
      <Box display='flex' alignItems='center'>
        <Switch
          checked={enabled}
          onChange={handleToggle}
          color='primary'
          inputProps={{ 'aria-label': 'enable address hiding' }}
        />
        <Box ml={1}>
          <Typography
            style={{
              fontWeight: 500,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Time-Based Hidden Addresses
          </Typography>
          <Typography variant='body2' style={{ fontSize: '13px', color: '#808080' }}>
            Hide activity addresses from volunteers outside a time window around each roster date
          </Typography>
        </Box>
      </Box>

      {enabled && (
        <Box mt={2} ml={7}>
          <Typography style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
            Reveal Address Before Roster Date
          </Typography>
          <Typography variant='body2' style={{ fontSize: '13px', color: '#808080', marginBottom: '8px' }}>
            Show the address this many days before each roster date. Leave empty to always show before.
          </Typography>
          <TextField
            type='number'
            value={revealDays}
            onChange={handleRevealDaysChange}
            variant='outlined'
            label='Days before'
            placeholder='e.g. 7'
            inputProps={{ min: 1, max: 30 }}
            style={{ width: 150 }}
            size='small'
            error={revealDays !== '' && !isValidRevealDays}
            helperText={revealDays !== '' && !isValidRevealDays ? 'Must be between 1 and 30' : ''}
          />

          <Box mt={2}>
            <Typography style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
              Hide Address After Roster Date
            </Typography>
            <Typography variant='body2' style={{ fontSize: '13px', color: '#808080', marginBottom: '8px' }}>
              Hide the address this many days after each roster date. Leave empty to hide once the roster date passes.
            </Typography>
            <TextField
              type='number'
              value={hideDays}
              onChange={handleHideDaysChange}
              variant='outlined'
              label='Days after'
              placeholder='e.g. 1'
              inputProps={{ min: 1, max: 30 }}
              style={{ width: 150 }}
              size='small'
              error={hideDays !== '' && !isValidHideDays}
              helperText={hideDays !== '' && !isValidHideDays ? 'Must be between 1 and 30' : ''}
            />
          </Box>

          <Box mt={3} display='flex' alignItems='center'>
            <Switch
              checked={removalEnabled}
              onChange={onRemovalChange}
              color='primary'
              inputProps={{ 'aria-label': 'enable address removal after event' }}
            />
            <Box ml={1}>
              <Typography
                style={{
                  fontWeight: 500,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Remove Roster Address After Event
              </Typography>
              <Typography variant='body2' style={{ fontSize: '13px', color: '#808080' }}>
                {
                  "Permanently remove the roster-specific address after the event date has passed. The activity's default address will still apply."
                }
              </Typography>
            </Box>
          </Box>

          <Box mt={3} display='flex' alignItems='center'>
            <Switch
              checked={allowDefaultEnabled}
              onChange={onAllowDefaultChange}
              color='primary'
              inputProps={{ 'aria-label': 'allow default address display' }}
            />
            <Box ml={1}>
              <Typography
                style={{
                  fontWeight: 500,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Allow Default Address Display
              </Typography>
              <Typography variant='body2' style={{ fontSize: '13px', color: '#808080' }}>
                {
                  "When enabled, the activity's default address will remain visible to volunteers even when address hiding is active."
                }
              </Typography>
            </Box>
          </Box>

          <Box mt={4}>
            <Typography style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
              Allow address visibility for:
            </Typography>
            <Typography variant='body2' style={{ fontSize: '13px', color: '#808080', marginBottom: '8px' }}>
              Choose which privilege levels can always see addresses, even when hiding is active.
            </Typography>

            <Box display='flex' alignItems='center' mt={1}>
              <Switch
                checked={exemptPm}
                onChange={onExemptPmChange}
                color='primary'
                inputProps={{ 'aria-label': 'exempt program managers from address hiding' }}
              />
              <Box ml={1}>
                <Typography style={{ fontWeight: 500, fontSize: '14px', color: '#333' }}>Program Managers</Typography>
              </Box>
            </Box>

            <Box display='flex' alignItems='center'>
              <Switch
                checked={exemptAl}
                onChange={onExemptAlChange}
                color='primary'
                inputProps={{ 'aria-label': 'exempt activity leaders from address hiding' }}
              />
              <Box ml={1}>
                <Typography style={{ fontWeight: 500, fontSize: '14px', color: '#333' }}>Activity Leaders</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {hasChanges && (
        <Box display='flex' justifyContent='flex-end' mt={3}>
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleSave}
            disabled={!canSave}
            style={{
              backgroundColor: theme.color.secondary.main900,
              color: '#ffffff',
              boxSizing: 'border-box',
              borderRadius: '4px',
              textTransform: 'none',
              padding: '7px 12px',
              minWidth: '40px',
            }}
          >
            Save
          </TabletButton>
        </Box>
      )}
    </Box>
  );
}
